import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Gauge,
  Activity,
  Zap,
  Database,
  Shield,
  RefreshCw,
  Server,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Layers,
  CircleDot,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function PerformancePage() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState("3");
  const [qpsHistory, setQpsHistory] = useState([]);
  const intervalRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/performance/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        setQpsHistory((prev) => {
          const now = new Date().toLocaleTimeString();
          const next = [...prev, { time: now, qps: data.qps.global_qps, bids: data.qps.total_bids, nobids: data.qps.total_nobids }];
          return next.slice(-60);
        });
      }
    } catch (err) {
      console.error("Failed to fetch performance stats:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchStats, parseInt(refreshInterval) * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, refreshInterval, fetchStats]);

  const handleInvalidateCache = async () => {
    try {
      await fetch(`${API}/api/performance/cache/invalidate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchStats();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="performance-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
      </div>
    );
  }

  const { qps, bid_logger, circuit_breakers, cache } = stats;
  const circuitEntries = Object.entries(circuit_breakers || {});
  const openCircuits = circuitEntries.filter(([, v]) => v.state === "open").length;
  const halfOpenCircuits = circuitEntries.filter(([, v]) => v.state === "half_open").length;

  return (
    <div className="space-y-6" data-testid="performance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="performance-title">
            Performance Monitor
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Real-time exchange throughput, latency, and system health
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={refreshInterval} onValueChange={setRefreshInterval}>
            <SelectTrigger className="w-[100px]" data-testid="refresh-interval-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1s</SelectItem>
              <SelectItem value="3">3s</SelectItem>
              <SelectItem value="5">5s</SelectItem>
              <SelectItem value="10">10s</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="auto-refresh-toggle"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${autoRefresh ? "animate-spin" : ""}`} />
            {autoRefresh ? "Live" : "Paused"}
          </Button>
        </div>
      </div>

      {/* Top metrics row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          testId="metric-global-qps"
          icon={Gauge}
          label="Current QPS"
          value={qps.global_qps.toFixed(1)}
          subtitle={`${qps.active_ssps} active SSP(s)`}
          accent="text-emerald-500"
        />
        <MetricCard
          testId="metric-total-requests"
          icon={Zap}
          label="Total Requests"
          value={qps.total_requests.toLocaleString()}
          subtitle={`${qps.bid_rate}% bid rate`}
          accent="text-blue-500"
        />
        <MetricCard
          testId="metric-buffer-size"
          icon={Database}
          label="Log Buffer"
          value={bid_logger.buffer_size}
          subtitle={`${bid_logger.total_flushed.toLocaleString()} flushed`}
          accent="text-amber-500"
        />
        <MetricCard
          testId="metric-circuits"
          icon={Shield}
          label="Circuit Breakers"
          value={`${circuitEntries.length} DSPs`}
          subtitle={openCircuits > 0 ? `${openCircuits} OPEN` : "All healthy"}
          accent={openCircuits > 0 ? "text-red-500" : "text-emerald-500"}
        />
      </div>

      {/* QPS Time Series */}
      <Card data-testid="qps-chart-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            QPS Over Time (60-second window)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {qpsHistory.length > 1 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={qpsHistory}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis dataKey="time" fontSize={11} tick={{ fill: "var(--muted-foreground)" }} interval="preserveStartEnd" />
                <YAxis fontSize={11} tick={{ fill: "var(--muted-foreground)" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Area type="monotone" dataKey="qps" stroke="#10b981" fill="#10b98130" strokeWidth={2} name="QPS" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
              Collecting data... QPS chart will appear after a few refresh cycles.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two-column: SSP Breakdown + Batch Logger */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* SSP QPS Breakdown */}
        <Card data-testid="ssp-qps-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="h-4 w-4" />
              QPS by SSP
            </CardTitle>
          </CardHeader>
          <CardContent>
            {qps.by_ssp && qps.by_ssp.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={qps.by_ssp} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis type="number" fontSize={11} tick={{ fill: "var(--muted-foreground)" }} />
                  <YAxis dataKey="ssp_name" type="category" fontSize={11} tick={{ fill: "var(--muted-foreground)" }} width={120} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Bar dataKey="qps" fill="#3b82f6" radius={[0, 4, 4, 0]} name="QPS" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
                No active SSPs receiving traffic.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Batch Logger Stats */}
        <Card data-testid="batch-logger-stats">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Batch Bid Logger
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <StatRow label="Buffer Size" value={bid_logger.buffer_size} icon={bid_logger.buffer_size > 1000 ? ArrowUpRight : ArrowDownRight} iconColor={bid_logger.buffer_size > 1000 ? "text-amber-500" : "text-emerald-500"} />
              <StatRow label="QPS Buffer" value={bid_logger.qps_buffer_size} />
              <StatRow label="Total Logged" value={bid_logger.total_logged.toLocaleString()} />
              <StatRow label="Total Flushed" value={bid_logger.total_flushed.toLocaleString()} />
              <StatRow label="Flush Count" value={bid_logger.flush_count} />
              <StatRow label="Last Batch" value={bid_logger.last_flush_size} />
            </div>
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Avg batch size</span>
                <span className="font-mono">
                  {bid_logger.flush_count > 0 ? Math.round(bid_logger.total_flushed / bid_logger.flush_count) : 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Circuit Breakers */}
      <Card data-testid="circuit-breakers-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4" />
              DSP Circuit Breakers
            </CardTitle>
            <div className="flex gap-2">
              {openCircuits > 0 && (
                <Badge variant="destructive">{openCircuits} Open</Badge>
              )}
              {halfOpenCircuits > 0 && (
                <Badge variant="outline" className="border-amber-500 text-amber-500">{halfOpenCircuits} Half-Open</Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {circuitEntries.length > 0 ? (
            <div className="space-y-2">
              {circuitEntries.map(([dspId, cb]) => (
                <CircuitBreakerRow key={dspId} dspId={dspId} stats={cb} token={token} onReset={fetchStats} />
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No circuit breakers active. Circuit breakers are created when DSPs are called in live mode.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cache + System Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="cache-stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4" />
              Config Cache
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <StatRow label="SSPs Cached" value={cache.ssp_cache_size} />
              <StatRow label="DSPs Cached" value={cache.dsp_cache_size} />
              <StatRow label="Active DSPs" value={cache.active_dsps_cached} />
            </div>
            <p className="text-xs text-muted-foreground">
              30s TTL with background refresh. Eliminates DB reads on the bid hot path.
            </p>
            <Button variant="outline" size="sm" onClick={handleInvalidateCache} data-testid="invalidate-cache-btn">
              <RefreshCw className="h-3 w-3 mr-1" /> Force Refresh
            </Button>
          </CardContent>
        </Card>

        <Card data-testid="architecture-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Architecture Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <ArchRow label="Config Lookups" value="In-memory TTL cache (30s)" status="fast" />
              <ArchRow label="Bid Logging" value="Async batch (1s flush, 2K buffer)" status="fast" />
              <ArchRow label="DSP Fan-out" value="Persistent pool (500 conn, 200 keepalive)" status="fast" />
              <ArchRow label="QPS Tracking" value="In-memory sliding window (60s)" status="fast" />
              <ArchRow label="DSP Health" value="Monitor-only (never blocks traffic)" status="protected" />
              <ArchRow label="MongoDB Indexes" value="Compound indexes on hot collections" status="optimized" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricCard({ testId, icon: Icon, label, value, subtitle, accent }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${accent}`}>{value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
          </div>
          <Icon className={`h-5 w-5 ${accent} opacity-60`} />
        </div>
      </CardContent>
    </Card>
  );
}

function StatRow({ label, value, icon: Icon, iconColor }) {
  return (
    <div className="flex items-center justify-between p-2 rounded bg-muted/30">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-mono font-medium flex items-center gap-1">
        {value}
        {Icon && <Icon className={`h-3 w-3 ${iconColor || ""}`} />}
      </span>
    </div>
  );
}

function CircuitBreakerRow({ dspId, stats: cb, token, onReset }) {
  const stateColor = {
    closed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    open: "bg-red-500/10 text-red-600 border-red-500/30",
    half_open: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  };

  const handleReset = async () => {
    try {
      await fetch(`${API}/api/performance/circuit-breakers/${dspId}/reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      onReset();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="flex items-center justify-between p-2.5 rounded-lg border bg-card" data-testid={`circuit-${dspId.slice(0, 8)}`}>
      <div className="flex items-center gap-3">
        <CircleDot className={`h-4 w-4 ${cb.state === "closed" ? "text-emerald-500" : cb.state === "open" ? "text-red-500" : "text-amber-500"}`} />
        <div>
          <p className="text-sm font-medium">{cb.dsp_name || dspId.slice(0, 12) + "..."}</p>
          <p className="text-xs text-muted-foreground">
            {cb.dsp_name ? <span className="font-mono">{dspId.slice(0, 12)}...</span> : null}
            {cb.dsp_name ? " · " : ""}{cb.total_requests} req, {cb.success_rate}% success
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="outline" className={stateColor[cb.state] || ""}>
          {cb.state.toUpperCase()}
        </Badge>
        {cb.state !== "closed" && (
          <Button variant="ghost" size="sm" onClick={handleReset} className="h-7 text-xs">
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}

function ArchRow({ label, value, status }) {
  const statusColor = {
    fast: "text-emerald-500",
    protected: "text-blue-500",
    optimized: "text-violet-500",
  };
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium text-xs ${statusColor[status] || ""}`}>{value}</span>
    </div>
  );
}
